import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { COUNTRIES } from "@/lib/content";

function initialValues(fields, initial) {
  const v = {};
  fields.forEach((f) => {
    if (f.type === "tel") v[f.name] = { dial: "+242", num: "" };
    else if (f.type === "checkboxes") v[f.name] = [];
    else v[f.name] = f.default ?? "";
  });
  return { ...v, ...initial, consent: false };
}

function PhoneField({ f, value, onChange, tid }) {
  const dials = [...new Set(COUNTRIES.map((c) => c[1]))];
  return (
    <div className="flex gap-2">
      <select aria-label="Indicatif" className="input !w-28" value={value.dial} data-testid={`${tid}-dial`}
        onChange={(e) => onChange({ ...value, dial: e.target.value })}>
        {dials.map((d) => <option key={d} value={d}>{d === "+" ? "Autre" : d}</option>)}
      </select>
      <input id={f.name} type="tel" inputMode="tel" className="input" required={f.required} placeholder="06 123 45 67"
        pattern="[0-9 ().\-]{6,20}" title="Chiffres uniquement" value={value.num} data-testid={tid}
        onChange={(e) => onChange({ ...value, num: e.target.value })} />
    </div>
  );
}

function FieldInput({ f, value, set, tid }) {
  const common = { id: f.name, required: f.required, "data-testid": tid };
  switch (f.type) {
    case "tel":
      return <PhoneField f={f} value={value} onChange={set} tid={tid} />;
    case "textarea":
      return <textarea {...common} rows={f.rows || 5} maxLength={5000} className="input" value={value} onChange={(e) => set(e.target.value)} />;
    case "country":
    case "select": {
      const opts = f.type === "country" ? COUNTRIES.map(([n]) => [n, n]) : f.options;
      return (
        <select {...common} className="input" value={value} onChange={(e) => set(e.target.value)}>
          <option value="">— Choisir —</option>
          {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      );
    }
    case "checkboxes":
      return (
        <div className="grid gap-2 sm:grid-cols-2" data-testid={tid}>
          {f.options.map((o, i) => (
            <label key={o} className="flex cursor-pointer items-start gap-2 rounded-md border border-black/10 bg-white p-2.5 text-sm hover:border-u">
              <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[var(--u)]" checked={value.includes(o)} data-testid={`${tid}-${i}`}
                onChange={(e) => set(e.target.checked ? [...value, o] : value.filter((x) => x !== o))} />
              {o}
            </label>
          ))}
        </div>
      );
    default:
      return <input {...common} type={f.type || "text"} min={f.min} max={f.max} maxLength={f.type === "number" ? undefined : 200}
        className="input" value={value} onChange={(e) => set(e.target.value)} />;
  }
}

function buildPayload(fields, values) {
  const payload = { details: {}, consent: values.consent };
  fields.forEach((f) => {
    let v = values[f.name];
    if (f.type === "tel") v = v.num ? `${v.dial === "+" ? "" : v.dial + " "}${v.num}`.trim() : "";
    if (f.detail) payload.details[f.name] = v;
    else payload[f.name] = v;
  });
  if (!Object.keys(payload.details).length) delete payload.details;
  return payload;
}

export function DynamicForm({ fields, endpoint, submitLabel = "Envoyer", initial = {}, testid, successText, renderSuccess }) {
  const [values, setValues] = useState(() => initialValues(fields, initial));
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const setField = (f) => (v) =>
    setValues((s) => {
      const next = { ...s, [f.name]: v };
      if (f.type === "country") {
        const tel = fields.find((x) => x.type === "tel");
        const dial = COUNTRIES.find((c) => c[0] === v)?.[1];
        if (tel && dial && !s[tel.name].num) next[tel.name] = { ...s[tel.name], dial };
      }
      return next;
    });

  const submit = async (e) => {
    e.preventDefault();
    const missing = fields.find((f) => f.required && f.type === "checkboxes" && !values[f.name].length);
    if (missing) return toast.error(`Veuillez cocher au moins une option : ${missing.label}`);
    if (!values.consent) return toast.error("Veuillez accepter la politique de protection des données.");
    setLoading(true);
    try {
      const { data } = await api.post(endpoint, buildPayload(fields, values));
      setDone(data);
      toast.success("Envoyé avec succès");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  if (done)
    return (
      <div className="card p-8 text-center" data-testid={`${testid}-success`} role="status">
        <CheckCircle2 className="mx-auto h-14 w-14 text-u" aria-hidden="true" />
        <p className="mt-4 font-display text-2xl font-bold uppercase text-ink">Merci !</p>
        <p className="mx-auto mt-2 max-w-md text-neutral-600">{successText || "Votre demande a bien été enregistrée. Un accusé de réception vous a été envoyé par e-mail."}</p>
        {renderSuccess?.(done)}
        <button className="btn-outline mt-6 text-u" onClick={() => { setDone(null); setValues(initialValues(fields, initial)); }} data-testid={`${testid}-reset`}>
          Envoyer une autre demande
        </button>
      </div>
    );

  return (
    <form onSubmit={submit} className="grid gap-5 sm:grid-cols-2" data-testid={testid} noValidate={false}>
      {fields.map((f) => (
        <div key={f.name} className={f.full || ["textarea", "checkboxes"].includes(f.type) ? "sm:col-span-2" : ""}>
          <label htmlFor={f.name} className="label">
            {f.label} {f.required && <span className="text-asso" aria-hidden="true">*</span>}
          </label>
          <FieldInput f={f} value={values[f.name]} set={setField(f)} tid={`${testid}-${f.name}`} />
          {f.hint && <p className="mt-1 text-xs text-neutral-500">{f.hint}</p>}
        </div>
      ))}
      <label className="flex items-start gap-3 rounded-md bg-u-soft p-4 text-sm text-neutral-800 sm:col-span-2">
        <input type="checkbox" className="mt-0.5 h-5 w-5 shrink-0 accent-[var(--u)]" checked={values.consent} required
          onChange={(e) => setValues((s) => ({ ...s, consent: e.target.checked }))} data-testid={`${testid}-consent`} />
        <span>
          J'accepte que mes données soient enregistrées et utilisées par MA LEBA uniquement pour traiter ma demande, conformément à la{" "}
          <Link to="/confidentialite" className="font-semibold text-u underline">politique de confidentialité</Link>. Aucune donnée médicale n'est collectée via ce formulaire.
        </span>
      </label>
      <div className="sm:col-span-2">
        <button type="submit" className="btn-u w-full sm:w-auto" disabled={loading} data-testid={`${testid}-submit`}>
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} {submitLabel}
        </button>
      </div>
    </form>
  );
}
